<template>
  <div>
    <h5>Exportar Registros de Entrada de um Evento</h5>
    <v-alert type="info" dense text>
      <b>Entenda o relatório</b>
      <p class="mb-0">
        O relatório exporta todos os registros de entrada de um evento.
      </p>
    </v-alert>
    <v-form v-model="valid" @submit.prevent>
      <v-row>
        <v-col cols="12">
          <party-selector
            v-model="party"
            :disabled="loading"
            :rules="[(v) => v || 'Selecione um evento']"
          />
        </v-col>
        <v-col cols="12">
          <v-select
            label="Status"
            multiple
            v-model="status"
            :items="statuses"
            outlined
            dense
            :rules="[(v) => v.length > 0 || 'Selecione pelo menos um status']"
          />
        </v-col>
      </v-row>
    </v-form>
    <v-alert v-if="error" type="error">
      {{ error }}
    </v-alert>
    <template v-else-if="loading">
      <v-progress-linear
        :indeterminate="totalTickets <= processedTickets"
        :value="(processedTickets / totalTickets) * 100"
        color="primary"
        height="10"
        rounded
      />
      <p class="text-caption text-center mt-1">
        Os registros de entrada estão sendo processados localmente, não feche a
        página.
      </p>
    </template>
    <v-alert v-else-if="succeeded" type="success">
      <b class="d-block">
        Processo finalizado com sucesso em {{ exportTime }} segundos
      </b>
      Foram processados {{ processedTickets | number }} registros de entrada
    </v-alert>
    <div class="d-flex justify-space-between">
      <v-btn @click="$emit('close')" :disabled="loading" text>Fechar</v-btn>
      <v-btn
        @click="exportProcess"
        :disabled="!valid"
        :loading="loading"
        color="primary"
      >
        Exportar
      </v-btn>
    </div>
  </div>
</template>

<script>
import FINANCIAL from "@/services/admin/financial";
import moment from "moment";
import ExcelJS from "exceljs";
import PartySelector from "@/components/global/selector/PartySelector.vue";

export const manifest = {
  name: "Exportar Registros de Entrada de um Evento",
  description: "Exporta todos os registros de entrada de um evento",
  icon: "mdi-list-box-outline",
  group: "party",
  formats: ["xlsx"],
};

export default {
  name: "ExportTicketFromParty",
  components: {
    PartySelector,
  },
  data: () => ({
    loading: false,
    error: false,
    valid: false,
    succeeded: false,
    totalTickets: 0,
    processedTickets: 0,
    exportTime: 0,
    status: ["succeeded", "rejected"],
    statuses: [
      { text: "Aprovado", value: "succeeded" },
      { text: "Recusado", value: "rejected" },
    ],
  }),
  methods: {
    async exportProcess() {
      const startTime = new Date();
      try {
        this.loading = true;
        this.error = false;
        this.succeeded = false;
        this.totalTickets = 0;
        this.processedTickets = 0;

        let canContinue = true;
        let skip = 0;
        const entriesList = [];
        while (canContinue) {
          const { entries, total } = await FINANCIAL.getEntries({
            skip,
            take: 5000,
            includeCode: true,
            partyId: this.party.id,
            status: this.status.join(","),
          });
          if (total) this.totalTickets = total;
          if (entries.length === 0) canContinue = false;
          entriesList.push(...entries);
          skip += entries.length;
          this.processedTickets += entries.length;
        }

        await this.exportExcel(entriesList);
        this.succeeded = true;
      } catch (error) {
        this.error = error.message || "Erro ao processar pagamentos";
        console.error(error);
      } finally {
        this.loading = false;
        this.exportTime = moment().diff(startTime, "seconds");
      }
    },
    async exportExcel(entries) {
      const workbook = new ExcelJS.Workbook();

      // Add metadata
      workbook.creator = "TiqOne";
      workbook.created = new Date();

      const hasCode = !!entries[0]?.Ticket?.code;

      const worksheet = workbook.addWorksheet("Registros de Entrada");
      worksheet.columns = [
        { header: "#", key: "id", width: 10 },
        { header: "#Ingresso", key: "ticketId", width: 10 },
        hasCode ? { header: "Código", key: "code", width: 20 } : null,
        { header: "Setor", key: "ticketGroup", width: 20 },
        { header: "Lote", key: "ticketBlock", width: 20 },
        { header: "Data", key: "createdAt", width: 20 },
        { header: "Status", key: "approved", width: 20 },
        { header: "Registrado por", key: "registerBy", width: 20 },
        { header: "Sessão", key: "session", width: 20 },
        { header: "Período", key: "period", width: 20 },
      ].filter(Boolean);

      for (const entry of entries) {
        const ticket = entry.Ticket;
        const ticketBlock = ticket.TicketBlock;
        const ticketGroup = ticketBlock.TicketGroup;

        worksheet.addRow({
          id: entry.id.slice(0, 8),
          ticketId: ticket.id.slice(0, 8),
          code: ticket.code || "ofuscado",
          ticketGroup: ticketGroup.name,
          ticketBlock: ticketBlock.name,
          createdAt: moment(entry.createdAt).format("DD/MM/YYYY HH:mm:ss"),
          approved: entry.approved ? "Aprovado" : "Recusado",
          registerBy: entry.RegisterBy?.name || entry.scannerName,
          session: entry.Session?.name,
          period: entry.Period?.name,
        });
      }
      // format date as date
      worksheet.getColumn("createdAt").style = {
        numFmt: "dd/mm/yyyy hh:mm:ss",
      };

      //   Save file
      workbook.xlsx.writeBuffer().then((buffer) => {
        const blob = new Blob([buffer], { type: "application/octet-stream" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Registros de Entrada - ${this.party.name}.xlsx`;
        a.click();
      });
    },
  },
  watch: {
    loading: {
      handler(val) {
        this.$emit("loading", val);
      },
    },
  },
  beforeDestroy() {
    this.$emit("loading", false);
  },
};
</script>
